import React, { useContext, useEffect, useState } from 'react';
import { LogisticasContainer } from './styles';
import { useTheme } from '../../../../contexts/Theme/ThemeContext';
import { Button } from '../../../Button';
import { blingApi, LogisticaBling, ServicoLogisticaBling } from '../../../../hooks/blingApi';
import { MelhorEnvioApi, TransportadoraME } from '../../../../hooks/melhorEnvio';
import Swal from 'sweetalert2';
import LoadingComFundo from '../../../Loading/LoadingComFundo/LoadingComFundo';
import { ConfiguracoesContext } from '../../../../contexts/Configuracoes/ConfiguracoesContext';
import { Select } from '../../../Select';

interface LogisticasProps {
    onClose: () => void;
}

interface FreteAtivo {
    chave: string;
    label: string;
    tipo: string;
}

const Logisticas: React.FC<LogisticasProps> = ({ onClose }) => {
    const blingApiHook = blingApi();
    const melhorEnvioApiHook = MelhorEnvioApi();
    const { configuracoes, setConfiguracoes, fretes_especiais } = useContext(ConfiguracoesContext);
    const { theme } = useTheme();

    const [loading, setLoading] = useState(true);
    const [logisticasBling, setLogisticasBling] = useState<LogisticaBling[]>([]);
    const [fretesAtivos, setFretesAtivos] = useState<FreteAtivo[]>([]);
    const [mapeamentoAux] = useState(
        configuracoes.dados_bling_v2?.mapeamento_logisticas || {}
    );

    const getFretesAtivos = (): FreteAtivo[] => {
        const fretes: FreteAtivo[] = [];
        const cfg = configuracoes.frete;

        // Correios
        if (cfg.correiosAtivo) {
            cfg.opcoes.forEach(opcao => {
                if (opcao.ativo && opcao.codigo) {
                    fretes.push({
                        chave: `correios_${opcao.codigo}`,
                        label: opcao.descricao || opcao.tipo.toUpperCase(),
                        tipo: 'correios'
                    });
                }
            });
        }

        // Fretes Especiais
        if (cfg.freteEspecialAtivo && fretes_especiais?.length) {
            fretes_especiais.filter(fe => fe.ativo).forEach(fe => {
                fretes.push({
                    chave: `frete_especial_${fe.id}`,
                    label: fe.descricao || `Frete Especial #${fe.id}`,
                    tipo: 'frete_especial'
                });
            });
        }

        // Frete Grátis
        if (cfg.freteGratisAtivo) {
            fretes.push({
                chave: 'frete_gratis',
                label: 'Frete Grátis',
                tipo: 'frete_gratis'
            });
        }

        // Entrega Local
        if (cfg.entregaLocal?.ativo) {
            fretes.push({
                chave: 'entrega_local',
                label: 'Entrega Local',
                tipo: 'entrega_local'
            });
        }

        // Retirada no Local
        if (cfg.retirada_no_local?.ativo) {
            fretes.push({
                chave: 'retirada_no_local',
                label: 'Retirada no Local',
                tipo: 'retirada_no_local'
            });
        }

        return fretes;
    };

    const getMelhorEnvioFretesAtivos = async (): Promise<FreteAtivo[]> => {
        const fretes: FreteAtivo[] = [];

        if (!configuracoes.frete.melhorEnvio?.ativo) return fretes;

        try {
            const transportadoras: TransportadoraME[] = await melhorEnvioApiHook.buscarTransportadoras();

            if (Array.isArray(transportadoras)) {
                transportadoras.forEach(transportadora => {
                    if (transportadora.services && Array.isArray(transportadora.services)) {
                        transportadora.services.forEach(servico => {
                            fretes.push({
                                chave: `melhor_envio_${transportadora.id}_${servico.id}`,
                                label: `${transportadora.name} - ${servico.name}`,
                                tipo: 'melhor_envio'
                            });
                        });
                    }
                });
            }
        } catch (error) {
            console.error('Erro ao buscar transportadoras do Melhor Envio:', error);
        }

        return fretes;
    };

    const buscarLogisticas = async () => {
        try {
            const response = await blingApiHook.buscarLogisticas();
            setLogisticasBling(response);
        } catch (error) {
            Swal.fire({
                title: 'Erro',
                text: 'Ocorreu um erro ao buscar as logísticas do Bling. Tente novamente.',
                icon: 'error',
                confirmButtonText: 'Ok'
            });
            onCancelar();
        }
    };

    const onCancelar = () => {
        setConfiguracoes({
            ...configuracoes,
            dados_bling_v2: {
                ...configuracoes.dados_bling_v2!,
                mapeamento_logisticas: mapeamentoAux
            }
        });
        onClose();
    };

    const handleChangeMapeamento = (chave: string, value: string) => {
        const novoMapeamento = {
            ...configuracoes.dados_bling_v2?.mapeamento_logisticas,
        };

        if (value === '') {
            delete novoMapeamento[chave];
        } else {
            novoMapeamento[chave] = value;
        }

        setConfiguracoes({
            ...configuracoes,
            dados_bling_v2: {
                ...configuracoes.dados_bling_v2!,
                mapeamento_logisticas: novoMapeamento
            }
        });
    };

    useEffect(() => {
        const init = async () => {
            await buscarLogisticas();

            const fretesBasicos = getFretesAtivos();
            const fretesMelhorEnvio = await getMelhorEnvioFretesAtivos();

            setFretesAtivos([...fretesBasicos, ...fretesMelhorEnvio]);
            setLoading(false);
        };
        init();
    }, []);

    // Monta lista de serviços de todas as logísticas usando aliases
    const getOptionsServicos = () => {
        const options: { name: string; value: string }[] = [
            { name: 'Não mapear', value: '' }
        ];

        logisticasBling.forEach(logistica => {
            if (logistica.servicos && Array.isArray(logistica.servicos)) {
                logistica.servicos
                    .filter(servico => servico.ativo)
                    .forEach(servico => {
                        // Usa o primeiro alias do serviço como value
                        const alias = servico.aliases && servico.aliases.length > 0
                            ? servico.aliases[0]
                            : servico.codigo || servico.descricao;

                        options.push({
                            name: `${logistica.descricao} - ${servico.descricao}`,
                            value: alias
                        });
                    });
            }
        });

        return options;
    };

    const optionsLogistica = getOptionsServicos();

    return (
        <LogisticasContainer theme={theme} loading={loading}>
            {loading ? (
                <LoadingComFundo />
            ) : (
                <>
                    <div className="titulo">
                        Mapear logísticas
                    </div>
                    <div className="texto">
                        Vincule os meios de frete do Conecta Venda às logísticas cadastradas no Bling.
                    </div>
                    {fretesAtivos.length === 0 ? (
                        <div className="aviso">
                            Nenhum meio de frete está ativo nas configurações.
                        </div>
                    ) : (
                        <div className="logisticas scrollBonito">
                            {fretesAtivos.map((frete) => (
                                <div key={frete.chave} className="logistica">
                                    <Select
                                        label={frete.label}
                                        placeholder="Selecione uma logística"
                                        options={optionsLogistica}
                                        value={
                                            configuracoes.dados_bling_v2?.mapeamento_logisticas?.[frete.chave] || ''
                                        }
                                        onChange={(value) => handleChangeMapeamento(frete.chave, value)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="botoes">
                        <div className="cancelar" onClick={onCancelar}>
                            Cancelar
                        </div>
                        <Button
                            title="Salvar"
                            onClick={onClose}
                        />
                    </div>
                </>
            )}
        </LogisticasContainer>
    );
};

export default Logisticas;